import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationAddButton } from "web_myplann/Inspiration/Components/InspirationAddButton";
import { InspirationRemainingTotal } from "web_myplann/Inspiration/Components/InspirationRemainingTotal";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { RetirementDropList } from "web_myplann/Inspiration/Retirement/Components/RetirementDropList";
import type { RetirementContext } from "web_myplann/Inspiration/Retirement/Types/RetirementTypes";
import { formatInspirationMoney } from "web_myplann/Inspiration/Utils/inspirationMoney";
import { LabeledValueRow } from "web_myplann/Shared/Components/LabeledValueRow";

const R = Resources.Inspiration.Retirement;

type Props = {
	context: RetirementContext;
	onToggleDrop: (id: string) => void;
	onRemoveDrop: (id: string) => void;
	onAddLoan: () => void;
};

export const RetirementExpensesSection: FC<Props> = ({
	context,
	onToggleDrop,
	onRemoveDrop,
	onAddLoan,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const divider = theme.colors.border.solid(theme.palette.grey[200]);

	return (
		<>
			<InspirationSection title={t(R.expensesTitle)}>
				<LabeledValueRow
					pb={theme.spacingTokens.md}
					borderBottom={divider}
					label={
						<Stack>
							<Typography variant="bodyMedium">
								{t(R.expensesTodayLabel)}
							</Typography>
							<Typography variant="caption" color={theme.palette.grey[500]}>
								{t(R.expensesTodayNote)}
							</Typography>
						</Stack>
					}
					value={
						<Typography variant="value" tabular>
							{formatInspirationMoney(context.monthlyExpenses ?? 0)}
						</Typography>
					}
				/>

				<RetirementDropList
					drops={context.drops}
					onToggle={onToggleDrop}
					onRemove={onRemoveDrop}
				/>

				<InspirationAddButton
					label={t(R.expensesAddLoan)}
					onClick={onAddLoan}
				/>

				<InspirationRemainingTotal
					pt={theme.spacingTokens.md}
					borderTop={divider}
					label={t(R.expensesRemainingLabel)}
					value={formatInspirationMoney(context.retirementExpenses ?? 0)}
					suffix={t(R.perMonth)}
				/>
			</InspirationSection>

			{!context.hasDeductibleDrops && (
				<Typography
					variant="hint"
					color={theme.palette.grey[500]}
					relaxed
					px={theme.spacingTokens.lg}
					display="block"
				>
					{t(R.expensesNoteEmpty)}
				</Typography>
			)}
		</>
	);
};
